import { DEFAULT_LOGO, MAX_MEMORY_HISTORY_DAYS } from 'constants/account';
import { EMPTY_ARRAY } from 'constants/defaults';
import type { CompanySizes } from 'redux/modules/accounts.schema';
import { EstimatedCompanySizes } from 'redux/modules/accounts.schema';
import type {
  Account,
  AccountResponse,
  Features,
  FeaturesPayload,
  FeaturesResponse,
  LogoInternal,
  UserAccountProperty,
} from 'types/account';

// rename server-side feature flags for clarity
function featureNameMap(featureName: keyof FeaturesResponse): keyof Features {
  if (featureName === 'memories') {
    return 'memory_history_days';
  }

  return featureName;
}

// Convert array of features to object /w sane values
function normalizeAccountFeatures(features: Array<FeaturesPayload>): Features {
  const props = (features || EMPTY_ARRAY).map(feature => {
    const { name, days } = feature;
    // All features are true, except memory history which specifies maximum past days of memories:
    let value = days !== -1 ? days : true;

    // If memories are unlimited, set them to 10 years. And please, let this
    // limitation bite me in the ass in 10 years.
    if (name === 'memories' && value === true) {
      value = MAX_MEMORY_HISTORY_DAYS;
    }

    return {
      name: featureNameMap(name),
      value: value as number | boolean,
    };
  });

  return props.reduce<Features>((result, item) => {
    if (item.name) {
      return {
        ...result,
        [item.name]: item.value,
      };
    }
    return result;
  }, {});
}

export function normalizeAccountPayload(account: AccountResponse): Account {
  return {
    ...account,
    features: normalizeAccountFeatures(account?.features),
  };
}

export function translateCompanySize(size: number = 1): CompanySizes {
  if (size > 1 && size <= 20) {
    return 10;
  } else if (size > 20 && size <= 99) {
    return 50;
  } else if (size > 99 && size <= 499) {
    return 300;
  } else if (size > 499) {
    return 500;
  }
  return 1;
}

export function translateEstimatedCompanySize(size: number) {
  if (size === 1) {
    return EstimatedCompanySizes.FREELANCER;
  }
  if (size < 20) {
    return EstimatedCompanySizes.TINY;
  }
  if (size < 50) {
    return EstimatedCompanySizes.XSMALL;
  }
  if (size < 250) {
    return EstimatedCompanySizes.SMALL;
  }
  if (size < 1000) {
    return EstimatedCompanySizes.MEDIUM;
  }
  if (size < 5000) {
    return EstimatedCompanySizes.LARGE;
  }
  if (size < 10000) {
    return EstimatedCompanySizes.XLARGE;
  }
  if (size < 50000) {
    return EstimatedCompanySizes.XXLARGE;
  }
  if (size < 100000) {
    return EstimatedCompanySizes.HUGE;
  }
  if (size > 100000) {
    return EstimatedCompanySizes.GIANT;
  }
  return EstimatedCompanySizes.FREELANCER;
}

export function createLogo(data: string | null): LogoInternal {
  return data
    ? {
        large_retina: data,
        medium_retina: data,
        small_retina: data,
        brand_logo: true,
      }
    : {
        ...DEFAULT_LOGO,
        delete_logo: true,
      };
}
/**
 * Helper function to map hour columns from state into the format expected by the ExportDropdown
 * component.
 */
export function mapExportColumns<T extends string = never>(
  columns: Array<T>,
  comparisonColumns: Array<T>,
  comparisonColumnsFilter?: (column: T) => boolean
): Record<T, boolean> {
  if (!comparisonColumnsFilter) {
    return comparisonColumns.reduce(
      (map, column) => {
        map[column] = columns.includes(column);
        return map;
      },
      {} as Record<T, boolean>
    );
  }

  return comparisonColumns.filter(comparisonColumnsFilter).reduce(
    (map, column) => {
      map[column] = columns.includes(column);
      return map;
    },
    {} as Record<T, boolean>
  );
}

export function generateUserAccountPropertiesChangeSet(
  originalUserAccountProperties: Array<UserAccountProperty>,
  userAccountProperties: Record<number, string> | undefined
) {
  if (!userAccountProperties) return [];
  const changeSet = [];
  for (const account_user_property_id in userAccountProperties) {
    const accountUserPropertyId = Number(account_user_property_id);
    const originalProperty = originalUserAccountProperties.find(
      p => p.account_user_property_id === accountUserPropertyId
    );
    const newValue = userAccountProperties[accountUserPropertyId] ?? '';
    if (originalProperty) {
      changeSet.push(
        newValue === ''
          ? {
              account_user_property_id: accountUserPropertyId,
              id: originalProperty.id,
              _destroy: 'true',
            }
          : {
              account_user_property_id: accountUserPropertyId,
              id: originalProperty.id,
              value: newValue,
            }
      );
    } else {
      changeSet.push({
        account_user_property_id: accountUserPropertyId,
        value: newValue,
      });
    }
  }
  return changeSet;
}
